'use client';

import { locales, type Locale } from '@/i18n';
import { createContext, useContext, useEffect, useState } from 'react';
import {
  resolvePreferredResponseLocale,
  storeLocalePreference,
} from '@/utils/language-utils';

type LanguageContextType = {
  currentLanguage: Locale;
  setCurrentLanguage: (language: Locale) => void;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({
  children,
  initialLocale = 'en',
}: {
  children: React.ReactNode;
  initialLocale?: Locale;
}) {
  const [currentLanguage, setCurrentLanguage] = useState<Locale>(() => {
    if (typeof window === 'undefined') {
      return initialLocale;
    }

    const resolvedLocale = resolvePreferredResponseLocale();
    return locales.includes(resolvedLocale) ? resolvedLocale : initialLocale;
  });

  // Save language preference when it changes
  useEffect(() => {
    // Store the current language preference
    storeLocalePreference(currentLanguage);
  }, [currentLanguage]);

  return (
    <LanguageContext.Provider value={{ currentLanguage, setCurrentLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
